<template>
  <div class="game-settings-container" :class="{ 'embedded-mode': isEmbedded }">
    <h1 v-if="!isEmbedded" class="game-title">{{ $t('Mazegame.MazegameTitle') }}</h1>

    <!-- 主題設定 -->
    <div class="setting-section">
      <div class="setting-title">{{ $t('Mazegame.topic') }}</div>
      <div class="vocabulary-counter">
        {{ topic.length }}/20
      </div>
      <input
        class="input-text"
        type="text"
        v-model="topic"
        :placeholder="$t('Mazegame.inputTopic')"
        maxlength="20"
      >
    </div>

    <!-- 題目列表 -->
    <div class="setting-section">
      <div class="setting-title">{{ $t('Mazegame.questionSettings') }}</div>
      <div class="vocabulary-counter">
        {{ questionList.length }}/10
      </div>

      <!-- 已新增的題目列表 -->
      <div v-for="(question, index) in questionList" :key="index" class="question-item">
        <div class="question-content">
          <div class="question-number">{{ index + 1 }}.</div>
          <div class="question-details">
            <div class="question-text">{{ question.text }}</div>
            <div class="question-options">
              <div
                v-for="(option, optIndex) in question.options"
                :key="optIndex"
                class="option-badge"
                :class="{ 'correct': option.isCorrect }"
              >
                {{ option.text }}
                <svg v-if="option.isCorrect" class="icon-svg correct-icon" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
        <button class="icon-button delete" @click="removeQuestion(index)">
          <svg class="icon-svg" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a1 1 0 01-1 1H3a1 1 0 110-2h14a1 1 0 011 1z"/>
          </svg>
        </button>
      </div>

      <!-- 新增題目表單 -->
      <div class="question-input">
        <div class="input-label">{{ $t('Mazegame.questionText') }}</div>
        <input
          class="input-text"
          type="text"
          v-model="newQuestion.text"
          :placeholder="$t('Mazegame.inputQuestionText')"
        >

        <div class="input-label">{{ $t('Mazegame.optionSettings') }}</div>

        <!-- 已新增的選項 -->
        <div class="options-list">
          <div
            v-for="(option, index) in newQuestion.options"
            :key="index"
            class="option-item"
          >
            <input
              type="text"
              v-model="option.text"
              class="option-input"
              :placeholder="$t('Mazegame.optionContent')"
            >
            <button
              class="correct-toggle"
              :class="{ 'active': option.isCorrect }"
              @click="toggleCorrectAnswer(index)"
              :title="$t('Mazegame.setCorrectAnswer')"
            >
              <svg class="icon-svg" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
              </svg>
            </button>
            <button
              class="icon-button small delete"
              @click="removeOption(index)"
              :disabled="newQuestion.options.length <= 2"
            >
              <svg class="icon-svg" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- 新增選項按鈕 -->
        <button
          class="add-option-button"
          @click="addOption"
          :disabled="newQuestion.options.length >= 4"
        >
          <svg class="icon-svg" viewBox="0 0 24 24">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M13 6C13 5.44771 12.5523 5 12 5C11.4477 5 11 5.44771 11 6V11H6C5.44771 11 5 11.4477 5 12C5 12.5523 5.44771 13 6 13H11V18C11 18.5523 11.4477 19 12 19C12.5523 19 13 18.5523 13 18V13H18C18.5523 13 19 12.5523 19 12C19 11.4477 18.5523 11 18 11H13V6Z" />
          </svg>
          {{ $t('Mazegame.addOption') }}
        </button>

        <!-- 新增題目按鈕 -->
        <button
          class="add-button"
          @click="addQuestion"
          :disabled="!canAddQuestion"
        >
          <svg class="icon-svg" viewBox="0 0 24 24">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M13 6C13 5.44771 12.5523 5 12 5C11.4477 5 11 5.44771 11 6V11H6C5.44771 11 5 11.4477 5 12C5 12.5523 5.44771 13 6 13H11V18C11 18.5523 11.4477 19 12 19C12.5523 19 13 18.5523 13 18V13H18C18.5523 13 19 12.5523 19 12C19 11.4477 18.5523 11 18 11H13V6Z" />
          </svg>
          {{ $t('Mazegame.addQuestion') }}
        </button>
      </div>
    </div>

    <!-- 引用 HiTeach 名單選項（僅在版本 >= 5.5.0.0002 時顯示） -->
    <div v-if="isNewPushFuncVersion" class="setting-section roster-section">
      <div class="roster-checkbox-wrapper">
        <label class="checkbox-container">
          <input type="checkbox" v-model="useHiTeachRoster" class="roster-checkbox" />
          <span class="checkbox-label">{{ $t('HTwbActKitHome.useHiTeachRoster') }}</span>
        </label>
      </div>
    </div>

    <!-- 操作按鈕（僅獨立模式顯示） -->
    <div v-if="!isEmbedded" class="action-buttons">
      <button class="clear-button" @click="clearData">{{ $t('Mazegame.clearData') }}</button>
      <button class="save-button" @click="saveSettings" :disabled="!canSaveSettings">
        {{ $t('Mazegame.saveSettings') }}
      </button>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, computed, inject, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useHiTeachVersion } from '@/composable/useHiTeachVersion';

const router = useRouter();
const { t } = useI18n();

// HiTeach 版本管理
const { htVersion, isNewPushFuncVersion, loadVersion } = useHiTeachVersion('HTMazeGameSetting');

// Props
const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({})
  },
  activityName: {
    type: String,
    default: ''
  },
  playerCount: {
    type: Number,
    default: 1
  },
  isEmbedded: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['update:modelValue']);

// 通信工具類
const MessageBridge = {
    ENV: {
        WEBVIEW: 'webview',
        WEB: 'web'
    },
    currentEnv: 'webview',
    init() {
        this.currentEnv = window?.chrome?.webview ? this.ENV.WEBVIEW : this.ENV.WEB;
        return this;
    },
    postMessage(data, origin = '*') {
        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.postMessage(data);
        } else {
            window.postMessage(data, origin);
        }
    },
    addEventListener(callback) {
        const wrappedCallback = (event) => callback(event);
        // 將包裝後的回調存儲起來，以便後續移除
        if (!this._listeners) {
            this._listeners = new WeakMap();
        }
        this._listeners.set(callback, wrappedCallback);

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.addEventListener('message', wrappedCallback);
        } else {
            window.addEventListener('message', wrappedCallback);
        }
    },
    // 移除消息監聽器
    removeEventListener(callback) {
        if (!this._listeners) {
            return;
        }
        const wrappedCallback = this._listeners.get(callback);
        if (!wrappedCallback) {
            return;
        }

        if (this.currentEnv === this.ENV.WEBVIEW) {
            window?.chrome?.webview.removeEventListener('message', wrappedCallback);
        } else {
            window.removeEventListener('message', wrappedCallback);
        }
        this._listeners.delete(callback);
    }
};

const messageBridge = MessageBridge.init();
const $Message = inject("$Message");

// 監聽器引用（用於清理）
let messageHandlerRef = null;

// 主題（優先使用 modelValue，其次使用 activityName，最後使用預設值）
const topic = ref(props.modelValue?.topic || props.activityName || 'Maze(Demo)');

// 題目列表（從 modelValue 初始化）
const questionList = ref(props.modelValue?.questionList || []);

// HiTeach 名單選項
const useHiTeachRoster = ref(false);

// 新題目
const newQuestion = ref({
  text: '',
  options: [
    { text: '', isCorrect: true },
    { text: '', isCorrect: false }
  ]
});

// 計算是否可以新增題目
const canAddQuestion = computed(() => {
  // 題目文字不能為空
  if (!newQuestion.value.text.trim()) return false;

  // 至少要有2個選項
  if (newQuestion.value.options.length < 2) return false;

  // 所有選項文字都不能為空
  const allOptionsFilled = newQuestion.value.options.every(opt => opt.text.trim() !== '');
  if (!allOptionsFilled) return false;

  // 必須有一個正確答案
  const hasCorrectAnswer = newQuestion.value.options.some(opt => opt.isCorrect);
  if (!hasCorrectAnswer) return false;

  return true;
});

// 計算是否可以儲存設置
const canSaveSettings = computed(() => {
  return questionList.value.length > 0 && topic.value.trim() !== '';
});

// 新增選項
const addOption = () => {
  if (newQuestion.value.options.length >= 4) {
    $Message.warning({
      content: t('Mazegame.maxOptions'),
      timeout: 2000,
    });
    return;
  }

  newQuestion.value.options.push({
    text: '',
    isCorrect: false
  });
};

// 移除選項
const removeOption = (index) => {
  if (newQuestion.value.options.length <= 2) {
    $Message.warning({
      content: t('Mazegame.minOptions'),
      timeout: 2000,
    });
    return;
  }

  newQuestion.value.options.splice(index, 1);
};

// 切換正確答案
const toggleCorrectAnswer = (index) => {
  // 單選題：取消所有其他選項的正確標記
  newQuestion.value.options.forEach((opt, i) => {
    opt.isCorrect = i === index;
  });
};

// 新增題目
const addQuestion = () => {
  if (!canAddQuestion.value) return;

  if (questionList.value.length >= 10) {
    $Message.warning({
      content: t('Mazegame.maxQuestions'),
      timeout: 2000,
    });
    return;
  }

  // 檢查是否有重複題目
  const isDuplicate = questionList.value.some(q => q.text === newQuestion.value.text.trim());
  if (isDuplicate) {
    $Message.warning({
      content: t('Mazegame.questionExists'),
      timeout: 2000,
    });
    return;
  }

  // 添加新題目
  questionList.value.push({
    text: newQuestion.value.text.trim(),
    options: JSON.parse(JSON.stringify(newQuestion.value.options))
  });

  // 重置表單
  newQuestion.value = {
    text: '',
    options: [
      { text: '', isCorrect: true },
      { text: '', isCorrect: false }
    ]
  };
};

// 移除題目
const removeQuestion = (index) => {
  questionList.value.splice(index, 1);
};

// 清空設定數據
const clearData = () => {
  topic.value = '';
  questionList.value = [];
  newQuestion.value = {
    text: '',
    options: [
      { text: '', isCorrect: true },
      { text: '', isCorrect: false }
    ]
  };

  // 清除 sessionStorage 中的配置
  sessionStorage.removeItem('MazeGameConfig');
};

// 儲存設置
const saveSettings = () => {
  if (!canSaveSettings.value) return;

  const gameSettings = {
    topic: topic.value,
    questionList: JSON.parse(JSON.stringify(questionList.value)),
    useHiTeachRoster: useHiTeachRoster.value
  };

  console.log('迷宮遊戲設定:', gameSettings);

  // 將設定同步儲存到 sessionStorage，供遊戲頁面與 Push 功能使用
  try {
    sessionStorage.setItem('MazeGameConfig', JSON.stringify(gameSettings));
  } catch (e) {
    console.error('儲存 MazeGameConfig 至 sessionStorage 失敗:', e);
  }

  // 發送設定到原生應用
  messageBridge.postMessage({
    action: 'Save.MazeGameSetting',
    data: gameSettings
  });

  $Message.success({
    content: t('Mazegame.settingsSaved'),
    timeout: 2000,
  });

  // 導航到遊戲頁面
  router.push('/ht-mazegame-play');
};

// 監聽主題變化，同步到父組件（嵌入模式）
watch(() => topic.value, () => {
  if (props.isEmbedded) {
    emitUpdate();
  }
});

// 監聽題目列表變化，同步到父組件（嵌入模式）
watch(() => questionList.value, () => {
  if (props.isEmbedded) {
    emitUpdate();
  }
}, { deep: true });

// 監聽活動名稱變化，同步到主題欄位
watch(() => props.activityName, (newName) => {
  if (newName && newName.trim() !== '' && !topic.value) {
    topic.value = newName;
  }
});

// 向父組件發送更新（僅嵌入模式）
const emitUpdate = () => {
  if (props.isEmbedded) {
    emit('update:modelValue', {
      topic: topic.value,
      questionList: JSON.parse(JSON.stringify(questionList.value)),
      useHiTeachRoster: useHiTeachRoster.value
    });
  }
};

// 初始化時立即向父組件更新（確保 gameParameters 有值）
if (props.isEmbedded) {
  emitUpdate();
}

// 從 sessionStorage 載入配置
onMounted(() => {
  // 保存監聽器引用以便在 onUnmounted 時移除
  const settingMessageHandler = (event) => {
    const msg = event.data;
    console.log(msg, 'msg');
  };
  messageBridge.addEventListener(settingMessageHandler);
  messageHandlerRef = settingMessageHandler;

  // 載入 HiTeach 版本號
  loadVersion();

  // 從 sessionStorage 讀取配置資料
  const savedConfig = sessionStorage.getItem('MazeGameConfig');
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig);
      console.log('從 sessionStorage 讀取到的配置:', config);

      // 填充表單欄位
      if (config.topic) topic.value = config.topic;
      if (config.questionList && Array.isArray(config.questionList)) {
        questionList.value = [...config.questionList];
      }
      if (typeof config.useHiTeachRoster !== 'undefined') {
        useHiTeachRoster.value = !!config.useHiTeachRoster;
      }
    } catch (error) {
      console.error('解析 sessionStorage 中的配置時出錯:', error);
    }
  }
});

// 組件卸載時清理監聽器
onUnmounted(() => {
  if (messageHandlerRef) {
    messageBridge.removeEventListener(messageHandlerRef);
    messageHandlerRef = null;
  }
});

// 暴露清空方法給父組件 (嵌入模式使用)
defineExpose({
  clearData
});
</script>

<style scoped>
.game-settings-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

/* 嵌入模式樣式 */
.game-settings-container.embedded-mode {
  max-width: 100%;
  margin: 0;
  padding: 0;
}

.game-title {
  font-size: 2rem;
  text-align: center;
  margin-bottom: 30px;
}

.setting-section {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  position: relative;
}

.setting-title {
  font-weight: bold;
  margin-bottom: 10px;
  font-size: 18px;
}

.vocabulary-counter {
  position: absolute;
  top: 15px;
  right: 15px;
  color: #666;
}

.input-text {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  margin-bottom: 10px;
}

.input-label {
  font-weight: 500;
  margin: 15px 0 8px 0;
  font-size: 15px;
  color: #333;
}

/* 題目列表樣式 */
.question-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background-color: white;
  padding: 15px;
  border-radius: 4px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.question-content {
  display: flex;
  flex: 1;
  gap: 10px;
}

.question-number {
  font-weight: bold;
  color: #666;
  min-width: 25px;
}

.question-details {
  flex: 1;
}

.question-text {
  font-weight: 500;
  margin-bottom: 8px;
  font-size: 16px;
}

.question-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.option-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  background-color: #e9ecef;
  border-radius: 16px;
  font-size: 14px;
}

.option-badge.correct {
  background-color: #d4edda;
  color: #155724;
  font-weight: 500;
}

.correct-icon {
  width: 14px;
  height: 14px;
  fill: #28a745;
}

/* 選項列表樣式 */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
}

.option-item {
  display: flex;
  gap: 8px;
  align-items: center;
}

.option-input {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.correct-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 2px solid #ddd;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.correct-toggle .icon-svg {
  width: 18px;
  height: 18px;
  fill: #999;
}

.correct-toggle.active {
  background-color: #28a745;
  border-color: #28a745;
}

.correct-toggle.active .icon-svg {
  fill: white;
}

.correct-toggle:hover {
  border-color: #28a745;
}

/* 圖標按鈕樣式 */
.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.icon-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.icon-button.delete .icon-svg {
  fill: #dc3545;
}

.icon-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.icon-button.small {
  width: 36px;
  height: 36px;
  border-radius: 4px;
}

.icon-svg {
  width: 20px;
  height: 20px;
}

/* 新增題目表單樣式 */
.question-input {
  display: flex;
  flex-direction: column;
  background-color: white;
  padding: 15px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.add-option-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
  margin-bottom: 15px;
}

.add-option-button .icon-svg {
  fill: white;
  width: 18px;
  height: 18px;
}

.add-option-button:hover {
  background-color: #5a6268;
}

.add-option-button:disabled {
  background-color: #ced4da;
  cursor: not-allowed;
}

.add-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.2s;
  height: 48px;
}

.add-button .icon-svg {
  fill: white;
  width: 24px;
  height: 24px;
}

.add-button:hover {
  background-color: #218838;
}

.add-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

/* 名單選項區塊 */
.roster-section {
  .roster-checkbox-wrapper {
    display: flex;
    align-items: center;
  }

  .checkbox-container {
    display: flex;
    align-items: center;
    cursor: pointer;
    user-select: none;

    .roster-checkbox {
      width: 20px;
      height: 20px;
      cursor: pointer;
      margin-right: 10px;
    }

    .checkbox-label {
      font-size: 1.2rem;
    }
  }
}

/* 操作按鈕樣式 */
.action-buttons {
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 30px;
}

.clear-button {
  background-color: #f44336;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-size: 1.1rem;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.2s;
}

.clear-button:hover {
  background-color: #da190b;
}

.save-button {
  background-color: #4caf50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-size: 1.1rem;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.2s;
}

.save-button:hover {
  background-color: #45a049;
}

.save-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}
</style>
